// src/utils/customerVisibilityScope.js
//
// Which customers a user may LIST, as Firestore filter sets — the same three tiers dataStore (active pipeline) and
// leadStore (lead pipeline) apply, for a caller that adds its own population filter (the Command Board adds
// `system.journey.section == …`). Each filter set is one query; the caller merges their results by id.
//
//   1. full visibility (the access matrix's collections.customers.fullVisibility)  → one query, no extra filter
//   2. organization owner                                                          → system.organizationId == theirs
//   3. sales / setter                                                              → system.ownerUids contains them,
//                                                                                    ∪ contact.submittedBy.uid == them
//   4. anyone else                                                                 → nothing
//
// dataStore and leadStore still carry their own copies of these tiers; they move onto this helper when the old
// dashboards retire (docs/features/PHASE97_COMMAND_BOARD_FEATURE.md S9).

import { where } from 'firebase/firestore'
import { OWNER_UIDS_FIELD } from '@/utils/customerOwnership'

/**
 * @param {object} args
 * @param {object|null} args.user        userStore.user
 * @param {string[]} args.roles          userStore.roles
 * @param {boolean} args.isSuperAdmin    userStore.isSuperAdmin
 * @param {object} args.permStore        usePermissionsStore() (initialized: await permStore.waitForInit())
 * @returns {{ tier: 'all'|'org'|'owned'|'none', filterSets: Array<Array<import('firebase/firestore').QueryConstraint>> }}
 */
export function customerScopeFilterSets({ user, roles = [], isSuperAdmin = false, permStore }) {
  if (!user) return { tier: 'none', filterSets: [] }
  if (permStore.hasFullVisibility('customers', roles, isSuperAdmin)) {
    return { tier: 'all', filterSets: [[]] }
  }
  if (user.orgRole === 'owner' && user.organizationId) {
    return {
      tier: 'org',
      filterSets: [[where('system.organizationId', '==', user.organizationId)]],
    }
  }
  if (roles.includes('sales') || roles.includes('setter')) {
    return {
      tier: 'owned',
      filterSets: [
        [where(OWNER_UIDS_FIELD, 'array-contains', user.uid)],
        [where('contact.submittedBy.uid', '==', user.uid)],
      ],
    }
  }
  return { tier: 'none', filterSets: [] }
}
